import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router-dom";

import { Api } from "@noom/noomscape";

import {
  EnrollmentSuccessType,
  ErrorCode,
  ErrorState,
  ProgramOffering,
  ResultType,
} from "@/constants";
import { isDoNotTrackPartner } from "@/constants/Partners";
import { useAppContext } from "@/contexts";
import { EnrollmentInformation, getDisplayName, User } from "@/models";
import { trackBuyflowConversion } from "@/utils/ConversionTracker";
import { validateAndGetCorrectedAddress } from "@/utils/address";
import { getCurriculumFromProgramOffering } from "@/utils/programs";
import { captureException } from "@/utils/sentry";
import { isBuyflowTraffic } from "@/utils/userSegment";

import { AxiosError } from "axios";

import { useEnrollMed } from "./useEnrollMed";
import { useEnrollmentEvent } from "./useEnrollmentEvent";
import { useExperiment } from "./useExperiment";
import { useGoto } from "./useGoto";
import { useQueryParams } from "./useQueryParams";
import { useTrackEvent } from "./useTrackEvent";

export const useCreateAccount = () => {
  const { partnerInfo, setEnrollmentInfo, setErrorState, setUserData } =
    useAppContext();
  const { enrollMed } = useEnrollMed();
  const { sendEnrolled } = useEnrollmentEvent();
  const { getExperimentAssignments } = useExperiment();
  const goto = useGoto();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { leadSource, eligibilityId } = useQueryParams();
  const {
    getAttributionEventFields,
    linkB2BIdentity,
    trackAnonEvent,
    trackIdentifiedEvent,
  } = useTrackEvent();

  const createAccount = async (
    upid: string,
    user: User,
    selectedProgram: ProgramOffering | null,
  ): Promise<string | undefined> => {
    const userData = { ...user };
    const curriculum = getCurriculumFromProgramOffering(selectedProgram);

    try {
      // Currently, we don't know which curriculum associated with a UPID.
      // As a workaround, always update the mapping if there are multiple curriculum choices.
      if (selectedProgram !== null) {
        await Api.call(
          "upid.updateUpidCurriculumMapping",
          Api.api.upid.updateUpidCurriculumMapping,
          {
            curriculum,
            upid,
          },
        );
      }

      userData.extras.address = await validateAndGetCorrectedAddress(
        userData.extras.address,
      );

      userData.extras = {
        ...userData.extras,
        eligibilityId,
        experimentAssignments: getExperimentAssignments(),
        partnerName: getDisplayName(partnerInfo),
      };

      // We need to get updated enrollment information in case the upid was moved to a new batch
      const updatedEnrollmentInfo: EnrollmentInformation = await Api.call(
        "upid.getEnrollmentInformation",
        Api.api.upid.getEnrollmentInformation,
        {
          upid,
        },
      );
      setEnrollmentInfo(updatedEnrollmentInfo);

      // cache userdata to enable use elsewhere (ie transitioning B2C->B2B)
      setUserData(userData);
      const { accessCode, wasCreated } = await Api.call(
        "account.create",
        Api.api.account.create,
        {
          upid,
          userData,
        },
      );
      sendEnrolled();

      if (selectedProgram === "MED") {
        await enrollMed(accessCode, userData);
      }

      // Set cookie expiration to 1 hour
      Cookies.set("lastEnrolledUpid", upid, { expires: 1 / 24 });

      linkB2BIdentity(accessCode, userData.email, upid);

      trackIdentifiedEvent("b2bEnrollmentIdentifiedSignupSubmitted", {
        b2bProgram: selectedProgram || "PROGRAM_UNSPECIFIED",
        b2bSignupResult: ResultType.SUCCESS,
        b2cTransitionedAccount: false,
        coursePacks: userData.coursePacks,
        freeAccountExists: !wasCreated,
        upid,
        ...getAttributionEventFields(),
      });

      trackAnonEvent("b2bEnrollmentAnonSignupSubmitted", {
        b2bProgram: selectedProgram || "PROGRAM_UNSPECIFIED",
        b2bSignupResult: ResultType.SUCCESS,
        b2cTransitionedAccount: false,
        coursePacks: userData.coursePacks,
        freeAccountExists: !wasCreated,
        leadSource,
      });

      const growthUserId = Cookies.get("_userId");
      if (
        partnerInfo &&
        !isDoNotTrackPartner(partnerInfo) &&
        isBuyflowTraffic() &&
        growthUserId
      ) {
        trackBuyflowConversion(curriculum, userData.email, growthUserId);
      }

      const successType = wasCreated
        ? EnrollmentSuccessType.CREATED
        : EnrollmentSuccessType.UPGRADED;
      goto.success(successType);
      return accessCode;
    } catch (e: unknown) {
      // Error codes from backend's WebApiErrorResponse
      const errorCode = (e as AxiosError<WebApiErrorResponse>)?.response?.data
        ?.errorCode;

      const existingB2BAccount =
        errorCode === "ALREADY_ENROLLED_IN_B2B_PROGRAM";
      const existingB2CAccount = errorCode === "NON_FREE_ACCOUNT_EXISTS";
      const pendingDeleteRequest = errorCode === "PENDING_DELETION_REQUEST";
      const claimed =
        errorCode === "ACTIVATION_CODE_CLAIMED" ||
        errorCode === "ACTIVATION_CODE_DOESNT_EXIST";
      const isIneligible = errorCode === "B2B_INELIGIBLE";
      const isBeforeGoLive = errorCode === "BEFORE_GO_LIVE";

      const medEnrollmentFailed =
        (e as Error)?.message === ErrorCode.MED_ENROLLMENT_FAILED;

      if (existingB2CAccount) {
        navigate({
          pathname: "/transition",
          search,
        });
        return undefined;
      }

      if (isBeforeGoLive) {
        setErrorState(ErrorState.BEFORE_GOLIVE_DATE);
        goto.error(ErrorState.BEFORE_GOLIVE_DATE);
        return undefined;
      }

      if (isIneligible) {
        throw new Error("B2B_INELIGIBLE");
      }

      let resultType = ResultType.ERROR;

      if (existingB2BAccount) {
        resultType = ResultType.ACCOUNT_EXISTS;
        setErrorState(ErrorState.ACCOUNT_EXISTS);
      } else if (claimed) {
        resultType = ResultType.CODE_CLAIMED;
        setErrorState(ErrorState.CODE_CLAIMED);
      } else if (pendingDeleteRequest) {
        resultType = ResultType.PENDING_DELETION_REQUEST;
        setErrorState(ErrorState.PENDING_DELETION_REQUEST);
      } else if (medEnrollmentFailed) {
        setErrorState(ErrorState.SUPPORT_REQUEST_CREATED);
      } else {
        setErrorState(ErrorState.DEFAULT);
      }

      trackIdentifiedEvent("b2bEnrollmentIdentifiedSignupSubmitted", {
        b2bProgram: selectedProgram || "PROGRAM_UNSPECIFIED",
        b2bSignupResult: resultType,
        b2cTransitionedAccount: false,
        coursePacks: userData.coursePacks,
        freeAccountExists: false,
        upid,
        ...getAttributionEventFields(),
      });

      trackAnonEvent("b2bEnrollmentAnonSignupSubmitted", {
        b2bProgram: selectedProgram || "PROGRAM_UNSPECIFIED",
        b2bSignupResult: resultType,
        b2cTransitionedAccount: false,
        coursePacks: userData.coursePacks,
        freeAccountExists: false,
        leadSource,
      });

      if (resultType === ResultType.ERROR) {
        captureException(e);
      }
      navigate("/error");
      return undefined;
    }
  };

  return {
    createAccount,
  };
};
